
import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { SupportTicket } from "@/lib/types/support-tickets";
import TicketStatusBadge from "./TicketStatusBadge";
import TicketPriorityBadge from "./TicketPriorityBadge";
import TicketTypeBadge from "./TicketTypeBadge";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { Link } from "react-router-dom";

interface TicketCardProps {
  ticket: SupportTicket;
}

const TicketCard: React.FC<TicketCardProps> = ({ ticket }) => {
  // Format the date
  const formattedDate = format(new Date(ticket.created_at), "MMM d, yyyy 'at' h:mm a");
  
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2 gap-2">
          <div>
            <h3 className="text-base font-title text-primary font-semibold line-clamp-1">{ticket.title}</h3>
            <div className="text-xs text-muted-foreground font-caption">
              Submitted by {ticket.creator_name} on {formattedDate}
            </div>
          </div>
          <TicketStatusBadge status={ticket.status} />
        </div>
        
        <p className="text-sm font-body mb-4 line-clamp-2 text-gray-700">
          {ticket.description || "No description provided."}
        </p>
        
        <div className="flex flex-wrap gap-2 items-center">
          <TicketTypeBadge type={ticket.ticket_type} />
          <TicketPriorityBadge priority={ticket.priority} />
          <div className="text-xs text-muted-foreground ml-auto">
            {ticket.assigned_to ? `Assigned to: ${ticket.assignee_name}` : 'Unassigned'}
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="bg-muted/30 p-2 pt-0 justify-end">
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-xs"
          asChild
        >
          <Link to={`/support/tickets/${ticket.id}`}>
            <Eye className="h-3.5 w-3.5 mr-1" />
            View Details
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default TicketCard;
